import { verifyDocument as verifyDocumentAPI } from 'api/documents'
import { dateFormat } from 'utils/format'
import { splitName } from 'utils/highlights'

import { BaseDocument, BaseDocumentJSON } from './BaseDocuments'
import { Config } from './Documents'
import { OcrRespItem, getHighlights } from './v2'

interface OrderFormConfig {
  headerBase: Config
  headerOrderer: Config
  headerSupplier: Config
  headerDelivery: Config
  other: Config
  itemLines: Config
}

interface OcrRespHeaderBase {
  orderFormDate: OcrRespItem
  orderFormDateN: string
  orderFormNo: OcrRespItem
  subjectTitle: OcrRespItem
  deliveryDate: OcrRespItem
  deliveryDateN: string
  paymentTerms: OcrRespItem
  totalAmountTaxIn: OcrRespItem
  totalAmountTaxEx: OcrRespItem
  totalTax: OcrRespItem
  taxInfo: OcrRespItem
  totalPrice8: OcrRespItem
  totalPrice10: OcrRespItem
  totalPrice: OcrRespItem
  subTotalPrice8: OcrRespItem
  subTotalPrice10: OcrRespItem
  subTotalPrice: OcrRespItem
  taxAmount8: OcrRespItem
  taxAmount10: OcrRespItem
  taxAmount: OcrRespItem
  taxAmountInfo: OcrRespItem
  discount: OcrRespItem
  currency: OcrRespItem
  otherDate: OcrRespItem
  otherDateN: string
  otherNum: OcrRespItem
  etcAmount: OcrRespItem
}

interface OcrRespOther {
  note: OcrRespItem
}

interface Company {
  companyName: OcrRespItem
  department: OcrRespItem
  personName: OcrRespItem
  postalCode: OcrRespItem
  address: OcrRespItem
  tel: OcrRespItem
  fax: OcrRespItem
}

interface OcrRespHeaderOrderer extends Company {
  owner: OcrRespItem
  email: OcrRespItem
  homepage: OcrRespItem
}
interface OcrRespHeaderSupplier extends Company {}
interface OcrRespHeaderDelivery extends Company {
  etc: OcrRespItem
}

interface OcrRespItemLinesItem {
  id: string
  order: number
  num: OcrRespItem
  name: OcrRespItem
  taxIncludedUnitPrice: OcrRespItem
  taxExcludedUnitPrice: OcrRespItem
  count: OcrRespItem
  quantityUnit: OcrRespItem
  date: OcrRespItem
  dateN: string | null
  taxExcludedPrice: OcrRespItem
  taxIncludedPrice: OcrRespItem
  taxAmount: OcrRespItem
  taxInfo: OcrRespItem
  discount: OcrRespItem
  currency: OcrRespItem
  etc: OcrRespItem
}

// https://wiki.navercorp.com/pages/viewpage.action?pageId=2391210238
interface OcrResp {
  headerBase: OcrRespHeaderBase
  headerOrderer: OcrRespHeaderOrderer
  headerSupplier: OcrRespHeaderSupplier
  headerDelivery: OcrRespHeaderDelivery
  other: OcrRespOther
  itemLines: OcrRespItemLinesItem[]
}

export const BASE_SECTIONS = [
  'headerBase',
  'headerOrderer',
  'headerSupplier',
  'headerDelivery',
  'other',
] as (keyof OcrResp)[]

export const OCR_RESP_ORDER_FORM_KEYS = [
  ...BASE_SECTIONS,
  'itemLines',
] as (keyof OcrResp)[]

interface OrderFormHeaderBase {
  orderFormDate: string
  orderFormDateN: string // Date. but not used
  orderFormNo: string
  subjectTitle: string
  deliveryDate: string
  deliveryDateN: string
  paymentTerms: string
  totalAmountTaxIn: number
  totalAmountTaxEx: number
  totalTax: number
  taxInfo: string
  totalPrice8: number
  totalPrice10: number
  totalPrice: number
  subTotalPrice8: number
  subTotalPrice10: number
  subTotalPrice: number
  taxAmount8: number
  taxAmount10: number
  taxAmount: number
  discount: number
  currency: string
  otherDate: string
  otherDateN?: string
  otherNum: string
  etcAmount: number
}

interface OrderFormHeaderCompany {
  companyName: string
  department: string
  personName: string
  postalCode: string
  address: string
  tel: string
  fax: string
}

interface OrderFormHeaderOrderer extends OrderFormHeaderCompany {
  owner: string
  email: string
  homepage: string
}

interface OrderFormHeaderSupplier extends OrderFormHeaderCompany {}

interface OrderFormHeaderDelivery extends OrderFormHeaderCompany {
  etc: string
}

interface OrderFormOther {
  note: string
}

interface OrderFormItemLine {
  id: string
  order: number
  num: string
  name: string
  taxIncludedUnitPrice: number
  taxExcludedUnitPrice: number
  count: number
  quantityUnit: string
  date: string
  dateN: string | null
  taxIncludedPrice: number
  taxExcludedPrice: number
  taxAmount: number
  taxInfo: string
  discount: number
  currency: string
  etc: string
}

interface OcrEditResult {
  headerBase: OrderFormHeaderBase
  headerOrderer: OrderFormHeaderOrderer
  headerSupplier: OrderFormHeaderSupplier
  headerDelivery: OrderFormHeaderDelivery
  other: OrderFormOther
  itemLines: OrderFormItemLine[] | null // null vs []
}

export type DocumentOrderFormJSON = BaseDocumentJSON & {
  ocrResp: OcrResp
  ocrEditResult: OcrEditResult
  config: OrderFormConfig
}

export class DocumentOrderForm extends BaseDocument {
  constructor(public docJSON: DocumentOrderFormJSON) {
    super()
  }
  get ocrResp() {
    return this.docJSON.ocrResp
  }
  get ocrEditResult() {
    return this.docJSON.ocrEditResult
  }
  get config() {
    return this.docJSON.config
  }
  send() {
    throw Error('send of Order Form is out of spec')
  }
  async verify(edit: { [key: string]: any }) {
    const doc = this.docJSON.verifyData!.verifyData.map(({ fieldName, id }) => {
      const [category, field] = splitName(fieldName)
      const isItemLines = fieldName.match(/^itemLines/) && id
      const verifyInput = isItemLines
        ? edit[id]?.[field]
        : edit[category]?.[field]

      return {
        fieldName,
        id,
        data: verifyInput.toString(),
      }
    })
    return await verifyDocumentAPI(this.objectID, doc)
  }

  protected date2String(d: null | Date | string) {
    if (!d) return '' // null or undefiend??
    if (d instanceof Date) return !isNaN(d.getTime()) ? dateFormat(d) : ''
    else return d
  }

  private fixDates(newEdit: { [x: string]: any }) {
    // datepicker sets Date type
    newEdit.headerBase.orderFormDate = this.date2String(
      newEdit.headerBase.orderFormDate,
    )
    newEdit.headerBase.deliveryDate = this.date2String(
      newEdit.headerBase.deliveryDate,
    )
    newEdit.headerBase.otherDate = this.date2String(
      newEdit.headerBase.otherDate,
    )
    newEdit.itemLines = newEdit.itemLines?.map((l) => ({
      ...l,
      date: this.date2String(l.date),
    }))
  }

  async save(edit: { [x: string]: any }) {
    const newEdit = structuredClone(edit)
    this.ocrEditResult.itemLines = edit.itemLines // delete line can't be merged with es-toolkit merge
    this.fixDates(newEdit)
    return super.save(newEdit)
  }

  async confirm(edit: { [x: string]: any }) {
    this.fixDates(edit)
    await super.confirm(edit)
  }

  getHighlights() {
    return getHighlights(BASE_SECTIONS, LABELS, this.docJSON)
  }

  getLabel(section: string, fieldName: string) {
    return LABELS[section][fieldName]
  }
}

export const LABELS: {
  [section: string]: Record<string, string>
} = {
  headerBase: {
    orderFormDate: '注文書発行日',
    orderFormDateN: '注文書発行日N',
    orderFormNo: '注文書番号',
    subjectTitle: '件名',
    deliveryDate: '希望納期',
    deliveryDateN: '希望納期N',
    paymentTerms: '支払条件',
    totalAmountTaxIn: '合計金額(税込)',
    totalAmountTaxEx: '合計金額(税別)',
    totalTax: '税金額',
    taxInfo: '税金情報',
    totalPrice8: '小計金額(税込8%)',
    totalPrice10: '小計金額(税込10%)',
    totalPrice: '小計金額(税込)',
    subTotalPrice8: '小計金額(税別8%)',
    subTotalPrice10: '小計金額(税別10%)',
    subTotalPrice: '小計金額(税別)',
    taxAmount8: '小計税額(8%)',
    taxAmount10: '小計税額(10%)',
    taxAmount: '小計税額',
    taxAmountInfo: '小計税金情報',
    discount: '値引額',
    currency: '通貨単位',
    otherDate: 'その他日付',
    otherDateN: 'その他日付N',
    otherNum: 'その他番号',
    etcAmount: 'その他の商品金額',
  } as Record<keyof OrderFormHeaderBase, string>,
  headerOrderer: {
    companyName: '発行元会社名',
    department: '発行元担当部署名',
    personName: '発行元担当者名',
    owner: '発行元代表者名',
    postalCode: '発行元郵便番号',
    address: '発行元住所',
    tel: '発行元TEL',
    fax: '発行元FAX',
    email: '発行元メールアドレス',
    homepage: '発行元HP',
  } as Record<keyof OrderFormHeaderOrderer, string>,
  headerSupplier: {
    companyName: '注文先会社名',
    department: '注文先担当部署名',
    personName: '注文先担当者名',
    postalCode: '注文先郵便番号',
    address: '注文先住所',
    tel: '注文先TEL',
    fax: '注文先FAX',
  } as Record<keyof OrderFormHeaderSupplier, string>,
  headerDelivery: {
    companyName: '納入先会社名',
    department: '納入先担当部署名',
    personName: '納入先担当者名',
    postalCode: '納入先郵便番号',
    address: '納入先住所',
    tel: '納入先TEL',
    fax: '納入先FAX',
    etc: 'その他情報',
  } as Record<keyof OrderFormHeaderDelivery, string>,
  other: {
    note: '備考',
  } as Record<keyof OrderFormOther, string>,
  itemLines: {
    num: '品目番号',
    name: '品目',
    taxIncludedUnitPrice: '単価（税込）',
    taxExcludedUnitPrice: '単価（税別）',
    count: '個数',
    quantityUnit: '数量単位',
    date: '納入期日',
    taxIncludedPrice: '価格（税込）',
    taxExcludedPrice: '価格（税別）',
    taxAmount: '税金額',
    taxInfo: '税金情報',
    discount: '値引額',
    currency: '通貨単位',
    etc: 'その他の商品情報',
  } as Record<keyof OrderFormItemLine, string>,
} as const
